import React, { useState } from "react";
import { CertificateMatchList, type CertificateMatch } from "../components/LegacyCertList";
import { searchCertificates } from "../services/certificateSearch";

type Props = {
  active: boolean;
};

/**
 * Certificates page – unified search across Blancco, Certus och Legacy.
 * Uses same search helper as edit-modal but renders its own results list.
 */
export const CertificatesPage: React.FC<Props> = ({ active }) => {
  const [serial, setSerial] = useState("");
  const [assetId, setAssetId] = useState("");
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    blanccoHits: number;
    certusHits: number;
    legacyHits: number;
    matches: CertificateMatch[];
    warnings?: string[];
  } | null>(null);

  if (!active) return null;

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const assetIdNum = assetId.trim() ? Number(assetId.trim()) : undefined;
      const res = await searchCertificates({
        serial: serial.trim(),
        custom: custom.trim(),
        assetId: Number.isFinite(assetIdNum) ? (assetIdNum as number) : undefined,
        legacyLimit: 500,
      });
      setResult(res);
    } catch (e: any) {
      setError(e?.message || "Kunde inte hämta certifikat.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="mx-auto flex flex-col gap-4 w-full max-w-5xl">
        <div className="gw-card p-4 shadow-card border border-border">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="gw-h3 m-0">Raderingscertifikat</h2>
              <p className="text-sm text-muted m-0">
                Sök i Blancco, Certus och Legacy efter certifikat. Ange serienummer (substring) eller assetId.
              </p>
            </div>
          </div>

          <div className="grid gap-3 mt-4 md:grid-cols-2">
            <label className="gw-label flex flex-col gap-1">
              <span>Serienummer</span>
              <input
                className="gw-input"
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                placeholder="t.ex. PF1VKCSF"
              />
            </label>

            <label className="gw-label flex flex-col gap-1">
              <span>AssetID</span>
              <input
                className="gw-input"
                value={assetId}
                onChange={(e) => setAssetId(e.target.value)}
                placeholder="t.ex. 100123"
                inputMode="numeric"
              />
            </label>

            <label className="gw-label flex flex-col gap-1 md:col-span-2">
              <span>Custom (exakt sökning i Custom 1/2)</span>
              <input
                className="gw-input"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="t.ex. Shibuya"
              />
            </label>
          </div>

          <div className="mt-4 flex gap-3 flex-wrap">
            <button
              type="button"
              className="gw-btn gw-btn-primary"
              onClick={handleSearch}
              disabled={loading || (!serial.trim() && !assetId.trim() && !custom.trim())}
            >
              {loading ? "Söker..." : "Hämta certifikat"}
            </button>
            <button
              type="button"
              className="gw-btn gw-btn-ghost"
              onClick={() => {
                setSerial("");
                setAssetId("");
                setCustom("");
                setResult(null);
                setError(null);
              }}
              disabled={loading}
            >
              Rensa
            </button>
          </div>

          {error && <div className="mt-3 text-error text-sm">{error}</div>}
        </div>

        {result && (
          <div className="gw-card p-4 shadow-card border border-border space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <div className="gw-h4 m-0">Sökresultat</div>
                <div className="text-sm text-muted">
                  Blancco: {result.blanccoHits} · Certus: {result.certusHits} · Legacy: {result.legacyHits}
                </div>
              </div>
            </div>
            {result.warnings?.map((warning) => <div role="alert" className="text-error text-sm" key={warning}>{warning}</div>)}
            {result.matches.length === 0 ? (
              <div className="text-muted text-sm">Inga certifikat hittades.</div>
            ) : (
              <CertificateMatchList matches={result.matches} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
